import React, { createContext, forwardRef, useContext, useState } from 'react';
import { Box, Paper, Table as MuiTable, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Toolbar, Tooltip } from '@mui/material';
import { DensityMedium, DensitySmall } from '@mui/icons-material';
import Button from './Button';

const DensityContext = createContext({ dense: false });

export const ResultsCard = ({ children, className = '', ...props }) => {
  const [dense, setDense] = useState(false);
  return <DensityContext.Provider value={{ dense, setDense }}>
    <Paper elevation={0} className={'results-card sugi-results-card ' + className} {...props}>{children}</Paper>
  </DensityContext.Provider>;
};

export const ResultsToolbar = ({ children, className = '', ...props }) => {
  const { dense, setDense } = useContext(DensityContext);
  return <Toolbar className={'results-header sugi-results-toolbar ' + className} {...props}>
    <Box className="sugi-results-toolbar-content">{children}</Box>
    {setDense && <Tooltip title={dense ? 'Usar filas cómodas' : 'Usar filas compactas'}>
      <Button variant="text" size="sm" aria-label="Vista compacta" aria-pressed={dense} onClick={() => setDense(!dense)}
        startIcon={dense ? <DensityMedium /> : <DensitySmall />} sx={{ color: 'text.secondary', flexShrink: 0 }}>
        {dense ? 'Vista compacta' : 'Vista cómoda'}
      </Button>
    </Tooltip>}
  </Toolbar>;
};

// Upgrade the existing semantic table tree without changing data, paging or handlers.
const materialize = (children, header = false) => React.Children.map(children, child => {
  if (!React.isValidElement(child)) return child;
  const { children: inner, ...props } = child.props;
  if (child.type === React.Fragment) return <React.Fragment>{materialize(inner, header)}</React.Fragment>;
  if (child.type === 'thead') return <TableHead {...props}>{materialize(inner, true)}</TableHead>;
  if (child.type === 'tbody') return <TableBody {...props}>{materialize(inner)}</TableBody>;
  if (child.type === 'tr') return <TableRow hover={!header} {...props}>{materialize(inner, header)}</TableRow>;
  if (child.type === 'td' || child.type === 'th') {
    if (header && props.onClick) {
      const parts = React.Children.toArray(inner);
      const direction = parts.includes('↓') ? 'desc' : 'asc';
      const active = parts.includes('↑') || parts.includes('↓');
      const label = parts.filter(part => part !== '↑' && part !== '↓' && !(React.isValidElement(part) && (part.type?.muiName === 'SvgIcon' || part.type?.render?.muiName === 'SvgIcon')));
      const { onClick, ...cellProps } = props;
      return <TableCell component="th" scope="col" sortDirection={active ? direction : false} {...cellProps}>
        <TableSortLabel onClick={onClick} active={active} direction={direction}>{label}</TableSortLabel>
      </TableCell>;
    }
    return <TableCell component={child.type} scope={header ? 'col' : undefined} {...props}>{inner}</TableCell>;
  }
  return child;
});

const DataTable = forwardRef(({ children, className = '', size, striped, bordered, borderless, hover, responsive, preservePinnedColumns = false, ...props }, ref) => {
  const { dense } = useContext(DensityContext);
  return <MuiTable ref={ref} size={dense || size === 'sm' || size === 'small' ? 'small' : 'medium'}
    className={'sugi-data-table ' + (dense ? 'sugi-table-dense ' : '') + (preservePinnedColumns ? 'sugi-table-pinned ' : '') + className} {...props}>
    {materialize(children)}
  </MuiTable>;
});
DataTable.displayName = 'DataTable';
export default DataTable;
